import { useEffect, useState } from 'react';
import AccountSwitchHeader from '@/components/feature/AccountSwitchHeader';
import PeriodPicker, { monthRange } from '@/components/feature/PeriodPicker';
import type { Period } from '@/components/feature/PeriodPicker';
import { getBonusDetails } from '@/api';
import type { AccountDetails } from '@/api';

export default function BonusDetailsPage() {
  const [period, setPeriod] = useState<Period>(() => monthRange(0));
  const [bonusDetails, setBonusDetails] = useState<AccountDetails | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getBonusDetails(1, 50)
      .then((res) => {
        if (mounted) setBonusDetails(res);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : '加载失败');
      });
    return () => {
      mounted = false;
    };
  }, []);

  //按账期过滤记录并统计（收入、支出分开）
  const periodRecords = bonusDetails
    ? bonusDetails.records.filter((r) => {
        const day = r.date.replace(/\//g, '-').slice(0, 10);
        return day >= period.start && day <= period.end;
      })
    : [];
  const periodIncome = periodRecords.reduce(
    (s, r) => s + (r.direction === 2 ? 0 : r.amount),
    0,
  );
  const periodExpense = periodRecords.reduce(
    (s, r) => s + (r.direction === 2 ? r.amount : 0),
    0,
  );

  //加载中/失败
  if (!bonusDetails) {
    return (
      <div className="mobile-container pb-10">
        <AccountSwitchHeader current="bonus" />
        <div className="flex flex-col items-center justify-center py-24">
          {error ? (
            <>
              <i className="ri-wifi-off-line text-foreground-300 text-4xl mb-3" />
              <p className="text-foreground-400 text-sm">{error}</p>
            </>
          ) : (
            <>
              <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
              <p className="text-foreground-400 text-sm">加载中...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const hasRecords = periodRecords.length > 0;

  return (
    <div className="mobile-container pb-10">
      {/* Header */}
      <AccountSwitchHeader current="bonus" />

      <div className="px-4 space-y-4 pt-4">
        {/* Balance Card — green/gold gradient */}
        <div className="relative rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-green-500 to-amber-400"></div>
          {/* Decorative circles */}
          <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full bg-white/10"></div>
          <div className="absolute -bottom-6 right-10 w-20 h-20 rounded-full bg-white/8"></div>
          <div className="absolute top-6 right-20 w-10 h-10 rounded-full bg-white/12"></div>

          <div className="relative px-5 py-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs font-medium tracking-wide mb-1">我的奖金</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-white text-xl font-medium">¥</span>
                  <span className="text-white text-4xl font-bold tracking-tight">
                    {Number(bonusDetails.balance).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-coin-fill text-white text-xl"></i>
              </div>
            </div>

            {/* Stats row */}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">本期收入</p>
                <p className="text-white font-semibold text-sm">+¥{periodIncome.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">本期支出</p>
                <p className="text-white font-semibold text-sm">-¥{periodExpense.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">笔数</p>
                <p className="text-white font-semibold text-sm">{periodRecords.length} 笔</p>
              </div>
            </div>
          </div>
        </div>

        {/* Period Selector */}
        <PeriodPicker period={period} onChange={setPeriod} />

        {/* Section Title */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-emerald-500"></div>
            <span className="text-sm font-semibold text-foreground-800">奖金明细</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>
              收入：
              <span className="text-emerald-600 font-semibold">+¥{periodIncome.toFixed(2)}</span>
            </span>
            <span>
              支出：
              <span className="text-rose-500 font-semibold">-¥{periodExpense.toFixed(2)}</span>
            </span>
          </div>
        </div>

        {/* Records List */}
        {hasRecords ? (
          <div className="space-y-3">
            {periodRecords.map((record) => (
              <div
                key={record.id}
                className="bg-white rounded-2xl px-4 py-4 border border-background-100 hover:border-background-200 transition-colors"
              >
                {/* Top row */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full ${record.direction === 2 ? 'bg-rose-50' : 'bg-emerald-50'}`}>
                      <i className={`ri-coin-line text-sm ${record.direction === 2 ? 'text-rose-500' : 'text-emerald-500'}`}></i>
                    </div>
                    <div>
                      <span className="text-foreground-800 text-sm font-semibold">{record.type}</span>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${record.direction === 2 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
                          {record.direction === 2 ? '支出' : '收入'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className={`text-lg font-bold ${record.direction === 2 ? 'text-rose-500' : 'text-emerald-600'}`}>
                    {record.direction === 2 ? '-' : '+'}¥{Number(record.amount).toFixed(2)}
                  </p>
                </div>

                {/* Divider */}
                <div className="h-px bg-background-100 mb-3"></div>

                {/* Detail rows */}
                <div className="space-y-1.5">
                  {record.remark ? (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">备注</span>
                      <span className="text-foreground-700 font-medium">{record.remark}</span>
                    </div>
                  ) : null}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-400">时间</span>
                    <span className="text-foreground-500">{record.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="relative mb-6">
              <div className="w-24 h-24 rounded-full bg-emerald-50 flex items-center justify-center">
                <i className="ri-coin-line text-emerald-300 text-4xl"></i>
              </div>
              <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <i className="ri-search-line text-emerald-400 text-sm"></i>
              </div>
            </div>
            <p className="text-foreground-700 text-sm font-semibold mb-1.5">暂无明细记录</p>
            <p className="text-foreground-400 text-xs text-center leading-relaxed">
              本期暂无奖金发放记录
              <br />
              完成订单交易后可自动获得奖金
            </p>
          </div>
        )}

        {hasRecords && (
          <div className="flex items-center justify-center gap-2 py-3">
            <div className="h-px w-12 bg-background-200"></div>
            <span className="text-foreground-400 text-xs">已显示全部记录</span>
            <div className="h-px w-12 bg-background-200"></div>
          </div>
        )}
      </div>
    </div>
  );
}